import { nodeLength, isElement } from '../../../../common/src/ts/utils/dom';
import { DEFAULT_VOICE } from './env';

export interface Mapping {
  index: number;
  length: number;
  range: Range;
  replaced: boolean;
}

export interface SpeechItem {
  text: string;
  mappings: Mapping[];
}

export class SpeechFeedbackInfo {
  public constructor(public readonly originalEvent: SpeechSynthesisEvent,
                     public readonly currentNode?: Node,
                     public readonly currentOffset?: number) {}
}

export type SpeechFeedbackHandler = (this: SpeechText, info: SpeechFeedbackInfo) => any;

const holder: SpeechText[] = [];

export class SpeechText {
  private readonly uttrs: SpeechSynthesisUtterance[] = [];
  public rate: number = 1;
  public constructor(private readonly items: SpeechItem[]) {
    holder.push(this);
  }

  private createFeedbackEvent(e: SpeechSynthesisEvent, map?: Mapping) {
    if (map !== undefined) {
      let node = map.range.startContainer;
      let offset = map.range.startOffset;
      if (offset > 0 && offset === nodeLength(node) && node.nextSibling != null) {
        node = node.nextSibling;
        offset = 0;
      }
      while (isElement(node) && offset < nodeLength(node)) {
        node = node.childNodes[offset];
        offset = 0;
      }
      if (!map.replaced) {
        const { charIndex } = e;
        offset = Math.min(offset + charIndex - map.index, map.range.endOffset);
      }
      return new SpeechFeedbackInfo(e, node, offset);
    }
    return new SpeechFeedbackInfo(e);
  }

  private searchCurrentIndex(index: number, charIndex: number, mappings: Mapping[]) {
    for (let mapIndex = index; mapIndex < mappings.length; mapIndex += 1) {
      const map = mappings[mapIndex];
      if (map.index > charIndex) {
        return Math.max(mapIndex - 1, 0);
      }
    }
    return Math.max(mappings.length - 1, 0);
  }

  public speak() {
    for (const item of this.items) {
      const { text, mappings } = item;
      const uttr = new SpeechSynthesisUtterance(text);
      uttr.voice = DEFAULT_VOICE!;
      uttr.lang = 'ja-JP';
      let mapIndex = 0;
      const createCallback = (callback: () => SpeechFeedbackHandler | null) => {
        return (e: Event) => {
          const handler = callback();
          if (handler !== null) {
            const ev = e as SpeechSynthesisEvent;
            mapIndex = this.searchCurrentIndex(mapIndex, ev.charIndex, mappings);
            const map = mapIndex < mappings.length ? mappings[mapIndex] : undefined;
            handler.call(this, this.createFeedbackEvent(ev, map));
          }
        };
      };
      if (item === this.items[0]) {
        uttr.onstart = createCallback(() => this.onstart);
      }
      uttr.onboundary = createCallback(() => this.onboundary);
      uttr.onmark = createCallback(() => this.onmark);
      uttr.onpause = createCallback(() => this.onpause);
      uttr.onresume = createCallback(() => this.onresume);
      const onerror = createCallback(() => this.onerror);
      const unloadHanlder = () => {
        speechSynthesis.cancel();
      };
      uttr.onerror = (e) => {
        window.removeEventListener('beforeunload', unloadHanlder);
        onerror(e);
      };
      const onend = createCallback(() => this.onend);
      uttr.onend = (e) => {
        if (!speechSynthesis.pending || item === this.items[this.items.length - 1]) {
          window.removeEventListener('beforeunload', unloadHanlder);
          onend(e);
        }
      };
      uttr.rate = this.rate;
      this.uttrs.push(uttr);
      window.addEventListener('beforeunload', unloadHanlder);
      speechSynthesis.speak(uttr);
      if (speechSynthesis.paused) {
        speechSynthesis.resume();
      }
    }
  }

  public detach() {
    const index = holder.indexOf(this);
    if (index > 0) {
      holder[index] = holder[holder.length - 1];
      holder.pop();
    }
  }

  public onstart: SpeechFeedbackHandler | null = null;
  public onboundary: SpeechFeedbackHandler | null = null;
  public onmark: SpeechFeedbackHandler | null = null;
  public onpause: SpeechFeedbackHandler | null = null;
  public onresume: SpeechFeedbackHandler | null = null;
  public onerror: SpeechFeedbackHandler | null = null;
  public onend: SpeechFeedbackHandler | null = null;
}
